import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setTwoFactorRequired } from "./index";
import { shouldSendSessionActivityHeader } from "utils/sessionActivity";

const CACHE_SEC = 120;

const rawBaseQuery = fetchBaseQuery({
  baseUrl:
    process.env.REACT_APP_BASE_URL ||
    (process.env.NODE_ENV === "production" ? "" : "http://localhost:5001"),
  credentials: "include", // Include cookies for authentication
  prepareHeaders: (headers, { getState }) => {
    const viewAsRole = getState()?.global?.viewAsRole;
    if (viewAsRole) {
      headers.set("X-View-As-Role", viewAsRole);
    }
    if (shouldSendSessionActivityHeader()) {
      headers.set("X-Session-Activity", "1");
    }
    return headers;
  },
});

const shouldForceLogin = () => {
  const path = window.location?.pathname || "";
  return path !== "/login" && path !== "/home" && path !== "/";
};

const redirectToLoginOnExpiry = () => {
  if (!shouldForceLogin()) return;
  try {
    sessionStorage.setItem("logout", "true");
  } catch {
    /* ignore */
  }
  window.location.replace("/login?error=session_expired");
};

/** Client-side block: no RTK data fetches until SMS 2FA passes. */
const baseQueryWithAuth = async (args, api, extraOptions) => {
  if (api.getState()?.global?.twoFactorRequired) {
    return {
      error: {
        status: 403,
        data: {
          code: "TWO_FACTOR_REQUIRED",
          message: "Two-factor authentication required",
        },
      },
    };
  }

  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401) {
    // Nested-system auth failures (e.g. Endurova resume-ops session) reuse HTTP
    // 401 but are not this website's session — don't force a site-wide logout.
    const code = result.error?.data?.code;
    if (code !== "ENDUROVA_LOGIN_REQUIRED") {
      redirectToLoginOnExpiry();
    }
  }
  if (
    result.error?.status === 403 &&
    result.error?.data?.code === "TWO_FACTOR_REQUIRED"
  ) {
    api.dispatch(setTwoFactorRequired(true));
  }
  return result;
};

// Backend Api
export const api = createApi({
  baseQuery: baseQueryWithAuth, // base url
  reducerPath: "adminApi",
  // tags
  tagTypes: [
    "User",
    "Products",
    "Customers",
    "Transactions",
    "Geography",
    "Sales",
    "Admins",
    "Performance",
    "Dashboard",
    "TeamPresence",
    "Bidders",
    "BidderProgress",
    "BidderStats",
    "Applications",
    "Interviews",
    "Attendance",
    "ActivityLog",
    "Calendar",
    "Tasks",
    "Notifications",
    "Feedback",
    "WorkFeedback",
    "InterviewSchedules",
    "MyStats",
    "Callers",
    "CallerProfiles",
    "AdminInterviews",
    "Admin",
    "Invoices",
    "PaymentProfile",
    "ProfileMappings",
    "DoneRequests",
    "BidderOperations",
    "RoleProjects",
    "BankAccounts",
    "Ops",
    "Accounts",
    "TwoFactorPhones",
    "HiringRoles",
    "HiringAiConfig",
    "HiringInterviews",
    "ResumeOpsStatus",
    "ResumeOpsProfiles",
    "ResumeOpsWorkers",
    "ResumeOpsApplications",
    "ResumeOpsKeywords",
    "ResumeOpsSettings",
    "ResumeOpsOverview",
  ],
  keepUnusedDataFor: CACHE_SEC,
  refetchOnMountOrArgChange: CACHE_SEC,
  refetchOnFocus: false,
  // endpoints
  endpoints: (build) => ({
    getUser: build.query({
      query: (id) => `general/user/${id}`,
      providesTags: ["User"],
    }),
    getProducts: build.query({
      query: () => "client/products",
      providesTags: ["Products"],
    }),
    getCustomers: build.query({
      query: () => "client/customers",
      providesTags: ["Customers"],
    }),
    getTransactions: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "client/transactions",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Transactions"],
    }),
    getGeography: build.query({
      query: () => "client/geography",
      providesTags: ["Geography"],
    }),
    getSales: build.query({
      query: () => "sales/sales",
      providesTags: ["Sales"],
    }),
    getAdmins: build.query({
      query: () => "management/admins",
      providesTags: ["Admins"],
    }),
    getLeads: build.query({
      query: () => "management/leads",
      providesTags: ["Admins"],
    }),
    getCallers: build.query({
      query: () => "management/callers",
      providesTags: ["Callers"],
    }),
    updateCaller: build.mutation({
      query: ({ id, assignedProfiles }) => ({
        url: `management/callers/${id}`,
        method: "PATCH",
        body: { assignedProfiles },
      }),
      invalidatesTags: ["Callers"],
    }),
    deleteCaller: build.mutation({
      query: (id) => ({
        url: `management/callers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Callers"],
    }),
    getCallerProfiles: build.query({
      query: () => "management/caller-profiles",
      providesTags: ["CallerProfiles"],
    }),
    getUserPerformance: build.query({
      query: (id) => `management/performance/${id}`,
      providesTags: ["Performance"],
    }),
    getPerformanceDashboard: build.query({
      query: ({ period, from, to, role } = {}) => ({
        url: "management/performance-dashboard",
        params: { period, from, to, role },
      }),
      providesTags: ["Performance"],
    }),
    getCallerAssignments: build.query({
      query: ({ granularity = "week", startDate, endDate, anchor } = {}) => ({
        url: "management/caller-assignments",
        params: {
          granularity,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(anchor ? { anchor } : {}),
        },
      }),
      providesTags: ["Performance"],
    }),
    getDashboard: build.query({
      query: () => "general/dashboard",
      providesTags: ["Dashboard"],
    }),
    getDashboardStats: build.query({
      query: ({ period = "daily", from, to }) => ({
        url: "general/dashboard",
        params: {
          period,
          ...(from && to ? { from, to } : {}),
        },
      }),
      providesTags: (result, error, arg) => [
        { type: "Dashboard", id: arg.period },
        "Dashboard",
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}(${JSON.stringify(queryArgs)})`;
      },
      merge: (currentCache, newItems) => newItems,
    }),
    getAdminDashboard: build.query({
      query: (period) => ({
        url: "general/admin-dashboard",
        params: period ? { period } : {},
      }),
      providesTags: ["Dashboard"],
      keepUnusedDataFor: 120,
    }),
    getTeamPresence: build.query({
      query: () => "general/team-presence",
      providesTags: ["TeamPresence"],
      keepUnusedDataFor: 90,
    }),
    presenceHeartbeat: build.mutation({
      query: () => ({
        url: "general/presence-heartbeat",
        method: "POST",
      }),
    }),
    // ============================================
    // BIDDER ENDPOINTS
    // ============================================
    getBidders: build.query({
      query: () => "bidders",
      providesTags: ["Bidders"],
      keepUnusedDataFor: 120,
    }),
    updateMemberStatus: build.mutation({
      query: ({ id, status }) => ({
        url: `bidders/${id}/status`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["Bidders", "Notifications"],
    }),
    removeMember: build.mutation({
      query: ({ id, reason, isRequest }) => ({
        url: `management/members/${id}`,
        method: "DELETE",
        body: { reason, isRequest },
      }),
      invalidatesTags: ["Bidders", "Notifications"],
    }),
    getBidderById: build.query({
      query: (id) => `bidders/${id}`,
      providesTags: (result, error, id) => [{ type: "Bidders", id }],
    }),
    getBidderMetrics: build.query({
      query: ({ id, period = "monthly" }) => ({
        url: `bidders/${id}/metrics`,
        params: { period },
      }),
      providesTags: (result, error, { id }) => [
        { type: "BidderStats", id },
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}(${JSON.stringify(queryArgs)})`;
      },
    }),
    getBidderActivity: build.query({
      query: ({ id, limit = 100, skip = 0 }) => ({
        url: `bidders/${id}/activity`,
        params: { limit, skip },
      }),
      providesTags: (result, error, { id }) => [
        { type: "ActivityLog", id },
      ],
    }),
    getSummaryReport: build.query({
      query: ({ period = "monthly", startDate, endDate }) => ({
        url: "bidders/reports/summary",
        params: { period, startDate, endDate },
      }),
      providesTags: ["Bidders"],
      keepUnusedDataFor: 120,
    }),
    // Mutations
    logApplication: build.mutation({
      query: ({ id, date, count, notes }) => ({
        url: `bidders/${id}/applications`,
        method: "POST",
        body: { date, count, notes },
      }),
      invalidatesTags: (result, error, { id }) => [
        "Bidders",
        { type: "BidderStats", id },
        { type: "Applications", id },
      ],
    }),
    logInterview: build.mutation({
      query: ({ id, date, count, notes }) => ({
        url: `bidders/${id}/interviews`,
        method: "POST",
        body: { date, count, notes },
      }),
      invalidatesTags: (result, error, { id }) => [
        "Bidders",
        { type: "BidderStats", id },
        { type: "Interviews", id },
      ],
    }),
    recordAttendance: build.mutation({
      query: ({ id, date, status, notified, notes }) => ({
        url: `bidders/${id}/attendance`,
        method: "POST",
        body: { date, status, notified, notes },
      }),
      invalidatesTags: (result, error, { id }) => [
        "Bidders",
        "BidderProgress",
        { type: "BidderStats", id },
        { type: "Attendance", id },
      ],
    }),
    getBidderProgress: build.query({
      query: ({ bidderId, periodType, from, to } = {}) => ({
        url: "bidders/progress",
        params: { bidderId, periodType, from, to },
      }),
      providesTags: ["BidderProgress"],
    }),
    getBidderProgressAggregate: build.query({
      query: ({ period = "weekly", startDate, endDate } = {}) => ({
        url: "bidders/progress/aggregate",
        params: { period, from: startDate, to: endDate },
      }),
      providesTags: ["BidderProgress"],
      keepUnusedDataFor: 120,
    }),
    createBidderProgress: build.mutation({
      query: (body) => ({
        url: "bidders/progress",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BidderProgress", "Bidders", "BidderOperations", "Dashboard"],
    }),
    updateBidderProgress: build.mutation({
      query: ({ id, ...body }) => ({
        url: `bidders/progress/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["BidderProgress", "Bidders", "BidderOperations", "Dashboard"],
    }),
    deleteBidderProgress: build.mutation({
      query: (id) => ({
        url: `bidders/progress/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BidderProgress", "Bidders", "BidderOperations", "Dashboard"],
    }),
    bulkSaveBidderProgress: build.mutation({
      query: (body) => ({
        url: "bidders/progress/bulk-save",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BidderProgress", "Bidders", "BidderOperations", "Dashboard"],
    }),
    // Register new member (admin only)
    registerUser: build.mutation({
      query: (userData) => ({
        url: "auth/register",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["Bidders", "User"],
    }),
    // Calendar endpoints
    getCalendarAuthUrl: build.query({
      query: () => ({
        url: "api/calendar/auth/url",
      }),
    }),
    getCalendarStatus: build.query({
      query: () => ({
        url: "api/calendar/status",
      }),
      providesTags: ["Calendar"],
    }),
    getTeamCalendarEvents: build.query({
      query: ({ timeMin, timeMax }) => ({
        url: "api/calendar/team",
        params: { timeMin, timeMax },
      }),
      providesTags: ["Calendar"],
    }),
    disconnectCalendar: build.mutation({
      query: () => ({
        url: "api/calendar/disconnect",
        method: "POST",
      }),
      invalidatesTags: ["Calendar"],
    }),
    syncCalendar: build.mutation({
      query: () => ({
        url: "api/calendar/sync-all",
        method: "POST",
      }),
      invalidatesTags: ["Calendar", "Bidders", "Interviews"],
    }),
    updateCalendarColor: build.mutation({
      query: ({ userId, color }) => ({
        url: "api/calendar/color",
        method: "POST",
        body: { userId, color },
      }),
      invalidatesTags: ["Calendar"],
    }),
    // Admin endpoints
    getSalaryData: build.query({
      query: ({ period = "monthly", from, to }) => ({
        url: "admin/salary",
        params: { 
          period,
          ...(from && to ? { from, to } : {}),
        },
      }),
      providesTags: ["Admin"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}(${JSON.stringify(queryArgs)})`;
      },
    }),
    getApplicationsInterviews: build.query({
      query: ({ period = "monthly", from, to }) => ({
        url: "admin/applications-interviews",
        params: { 
          period,
          ...(from && to ? { from, to } : {}),
        },
      }),
      providesTags: ["Admin"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}(${JSON.stringify(queryArgs)})`;
      },
    }),
    getAttendanceData: build.query({
      query: ({ period = "monthly", from, to }) => ({
        url: "admin/attendance",
        params: { 
          period,
          ...(from && to ? { from, to } : {}),
        },
      }),
      providesTags: ["Admin"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}(${JSON.stringify(queryArgs)})`;
      },
    }),
    // Invoice endpoints
    getInvoices: build.query({
      query: ({ status, type } = {}) => ({
        url: "invoices",
        params: { ...(status && status !== "all" ? { status } : {}), ...(type ? { type } : {}) },
      }),
      providesTags: ["Invoices"],
    }),
    getInvoiceById: build.query({
      query: (id) => ({
        url: `invoices/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: "Invoices", id }],
    }),
    getInvoiceAnalytics: build.query({
      query: ({ period = "monthly", from, to }) => ({
        url: "invoices/analytics",
        params: { 
          period,
          ...(from && to ? { from, to } : {}),
        },
      }),
      providesTags: ["Invoices", "Admin"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}(${JSON.stringify(queryArgs)})`;
      },
    }),
    createInvoice: build.mutation({
      query: (invoiceData) => ({
        url: "invoices",
        method: "POST",
        body: invoiceData,
      }),
      invalidatesTags: ["Invoices", "PaymentProfile"],
    }),
    approveInvoice: build.mutation({
      query: ({ id, adminNote }) => ({
        url: `invoices/${id}/approve`,
        method: "PATCH",
        body: { adminNote },
      }),
      invalidatesTags: ["Invoices", "Admin", "CashFlow"],
    }),
    declineInvoice: build.mutation({
      query: ({ id, adminNote, reason }) => ({
        url: `invoices/${id}/decline`,
        method: "PATCH",
        body: { adminNote, reason },
      }),
      invalidatesTags: ["Invoices", "Admin"],
    }),
    requestInvoicePayout: build.mutation({
      query: (id) => ({
        url: `invoices/${id}/request-payout`,
        method: "POST",
      }),
      invalidatesTags: ["Invoices", "Admin", "CashFlow"],
    }),
    markInvoicePaid: build.mutation({
      query: ({ id, ...body }) => ({
        url: `invoices/${id}/mark-paid`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Invoices", "Admin", "CashFlow"],
    }),
    cancelInvoice: build.mutation({
      query: ({ id, reason }) => ({
        url: `invoices/${id}/cancel`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: ["Invoices", "Admin"],
    }),
    getInvoiceActivity: build.query({
      query: (id) => `invoices/${id}/activity`,
      providesTags: (result, error, id) => [{ type: "Invoices", id }],
    }),
    getPaymentProfile: build.query({
      query: ({ asset = "USDT", networkCode = "ERC20" } = {}) => ({
        url: "invoices/payment-profile",
        params: { asset, networkCode },
      }),
      providesTags: ["PaymentProfile"],
    }),
    upsertPaymentProfile: build.mutation({
      query: (body) => ({
        url: "invoices/payment-profile",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["PaymentProfile"],
    }),
    getFinanceOverview: build.query({
      query: () => "invoices/finance/overview",
      providesTags: ["Invoices"],
    }),
    createCallerInvoice: build.mutation({
      query: (data) => ({
        url: "invoices/caller",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Invoices", "PaymentProfile"],
    }),
    updateInvoice: build.mutation({
      query: ({ id, ...data }) => ({
        url: `invoices/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Invoices"],
    }),
    submitInvoice: build.mutation({
      query: (id) => ({
        url: `invoices/${id}/submit`,
        method: "PATCH",
      }),
      invalidatesTags: ["Invoices"],
    }),
    // Cash Flow endpoints (income + outgoing crypto payments + sectors)
    getCashFlow: build.query({
      query: () => "finance/cash-flow",
      providesTags: ["CashFlow"],
    }),
    createIncome: build.mutation({
      query: (data) => ({ url: "finance/income", method: "POST", body: data }),
      invalidatesTags: ["CashFlow", "Invoices"],
    }),
    updateIncome: build.mutation({
      query: ({ id, ...data }) => ({ url: `finance/income/${id}`, method: "PATCH", body: data }),
      invalidatesTags: ["CashFlow", "Invoices"],
    }),
    deleteIncome: build.mutation({
      query: (id) => ({ url: `finance/income/${id}`, method: "DELETE" }),
      invalidatesTags: ["CashFlow", "Invoices"],
    }),
    createCryptoPayment: build.mutation({
      query: (data) => ({ url: "finance/crypto-payments", method: "POST", body: data }),
      invalidatesTags: ["CashFlow", "Invoices"],
    }),
    importCryptoPayments: build.mutation({
      query: (data) => ({ url: "finance/crypto-payments/import", method: "POST", body: data }),
      invalidatesTags: ["CashFlow", "Invoices"],
    }),
    updateCryptoPayment: build.mutation({
      query: ({ id, ...data }) => ({ url: `finance/crypto-payments/${id}`, method: "PATCH", body: data }),
      invalidatesTags: ["CashFlow", "Invoices"],
    }),
    deleteCryptoPayment: build.mutation({
      query: (id) => ({ url: `finance/crypto-payments/${id}`, method: "DELETE" }),
      invalidatesTags: ["CashFlow", "Invoices"],
    }),
    bulkDeleteCashFlow: build.mutation({
      query: (data) => ({ url: "finance/cash-flow/bulk-delete", method: "POST", body: data }),
      invalidatesTags: ["CashFlow", "Invoices"],
    }),
    getPaymentSectors: build.query({
      query: () => "finance/payment-sectors",
      providesTags: ["CashFlow"],
    }),
    createPaymentSector: build.mutation({
      query: (data) => ({ url: "finance/payment-sectors", method: "POST", body: data }),
      invalidatesTags: ["CashFlow"],
    }),
    updatePaymentSector: build.mutation({
      query: ({ id, ...data }) => ({ url: `finance/payment-sectors/${id}`, method: "PATCH", body: data }),
      invalidatesTags: ["CashFlow"],
    }),
    deletePaymentSector: build.mutation({
      query: (id) => ({ url: `finance/payment-sectors/${id}`, method: "DELETE" }),
      invalidatesTags: ["CashFlow"],
    }),
    // Account Management (SaaS / subscriptions)
    getAccounts: build.query({
      query: () => "finance/accounts",
      providesTags: ["Accounts"],
    }),
    getAccountsSummary: build.query({
      query: () => "finance/accounts/summary",
      providesTags: ["Accounts"],
    }),
    createAccount: build.mutation({
      query: (data) => ({ url: "finance/accounts", method: "POST", body: data }),
      invalidatesTags: ["Accounts"],
    }),
    updateAccount: build.mutation({
      query: ({ id, ...data }) => ({ url: `finance/accounts/${id}`, method: "PATCH", body: data }),
      invalidatesTags: ["Accounts"],
    }),
    deleteAccount: build.mutation({
      query: (id) => ({ url: `finance/accounts/${id}`, method: "DELETE" }),
      invalidatesTags: ["Accounts"],
    }),
    // Task endpoints
    getFixedTasks: build.query({
      query: () => "tasks/fixed",
      providesTags: ["Tasks"],
    }),
    createFixedTask: build.mutation({
      query: (taskData) => ({
        url: "tasks/fixed",
        method: "POST",
        body: taskData,
      }),
      invalidatesTags: ["Tasks"],
    }),
    updateFixedTask: build.mutation({
      query: ({ id, ...taskData }) => ({
        url: `tasks/fixed/${id}`,
        method: "PUT",
        body: taskData,
      }),
      invalidatesTags: ["Tasks"],
    }),
    deleteFixedTask: build.mutation({
      query: (id) => ({
        url: `tasks/fixed/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tasks"],
    }),
    getAdditionalTasks: build.query({
      query: ({ date } = {}) => ({
        url: "tasks/additional",
        params: date ? { date } : {},
      }),
      providesTags: ["Tasks"],
    }),
    getAllAdditionalTasks: build.query({
      query: ({ date, userId } = {}) => ({
        url: "tasks/additional/all",
        params: { date, userId },
      }),
      providesTags: ["Tasks"],
    }),
    createAdditionalTask: build.mutation({
      query: (taskData) => ({
        url: "tasks/additional",
        method: "POST",
        body: taskData,
      }),
      invalidatesTags: ["Tasks", "Notifications"],
    }),
    updateTaskStatus: build.mutation({
      query: ({ id, status }) => ({
        url: `tasks/additional/${id}/status`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["Tasks"],
    }),
    updateKanbanColumn: build.mutation({
      query: ({ id, column }) => ({
        url: `tasks/additional/${id}/kanban`,
        method: "PUT",
        body: { column },
      }),
      invalidatesTags: ["Tasks"],
    }),
    notifyTaskUpdate: build.mutation({
      query: ({ id, status, description }) => ({
        url: `tasks/additional/${id}/notify`,
        method: "POST",
        body: { status, description },
      }),
      invalidatesTags: ["Tasks", "Notifications"],
    }),
    approveTaskUpdate: build.mutation({
      query: ({ id, updateIndex }) => ({
        url: `tasks/additional/${id}/approve`,
        method: "POST",
        body: { updateIndex },
      }),
      invalidatesTags: ["Tasks", "Notifications"],
    }),
    declineTaskUpdate: build.mutation({
      query: ({ id, updateIndex }) => ({
        url: `tasks/additional/${id}/decline`,
        method: "POST",
        body: { updateIndex },
      }),
      invalidatesTags: ["Tasks", "Notifications"],
    }),
    getNotifications: build.query({
      query: ({ unreadOnly } = {}) => ({
        url: "tasks/notifications",
        params: unreadOnly ? { unreadOnly: "true" } : {},
      }),
      providesTags: ["Notifications"],
    }),
    getNotificationCenter: build.query({
      query: ({ tab } = {}) => ({
        url: "tasks/notifications/center",
        params: tab && tab !== "all" ? { tab } : {},
      }),
      providesTags: ["Notifications"],
    }),
    getNotificationSummary: build.query({
      query: () => "tasks/notifications/summary",
      providesTags: ["Notifications"],
    }),
    getNotificationCount: build.query({
      query: () => ({
        url: "tasks/notifications",
        params: { countOnly: "true" },
      }),
      providesTags: ["Notifications"],
    }),
    markNotificationRead: build.mutation({
      query: (id) => ({
        url: `tasks/notifications/${id}/read`,
        method: "PUT",
      }),
      invalidatesTags: ["Notifications"],
    }),
    markAllNotificationsRead: build.mutation({
      query: () => ({
        url: "tasks/notifications/read-all",
        method: "PUT",
      }),
      invalidatesTags: ["Notifications"],
    }),
    notificationAction: build.mutation({
      query: ({ id, action, snoozeMinutes, assignTo }) => ({
        url: `tasks/notifications/${id}/action`,
        method: "PUT",
        body: { action, snoozeMinutes, assignTo },
      }),
      invalidatesTags: ["Notifications"],
    }),
    // Feedback endpoints
    submitFeedback: build.mutation({
      query: (feedbackData) => ({
        url: "feedback",
        method: "POST",
        body: feedbackData,
      }),
      invalidatesTags: ["Feedback"],
    }),
    getMyFeedbacks: build.query({
      query: () => "feedback/my",
      providesTags: ["Feedback"],
    }),
    getAllFeedbacks: build.query({
      query: ({ status } = {}) => ({
        url: "feedback",
        params: status ? { status } : {},
      }),
      providesTags: ["Feedback"],
    }),
    acceptFeedback: build.mutation({
      query: (id) => ({
        url: `feedback/${id}/accept`,
        method: "POST",
      }),
      invalidatesTags: ["Feedback"],
    }),
    replyToFeedback: build.mutation({
      query: ({ id, adminReply, status }) => ({
        url: `feedback/${id}/reply`,
        method: "PUT",
        body: { adminReply, status },
      }),
      invalidatesTags: ["Feedback"],
    }),
    getPendingUpdates: build.query({
      query: ({ status } = {}) => ({
        url: "feedback/pending-updates",
        params: status ? { status } : {},
      }),
      providesTags: ["Feedback"],
    }),
    updatePendingUpdate: build.mutation({
      query: ({ id, ...updateData }) => ({
        url: `feedback/pending-updates/${id}`,
        method: "PUT",
        body: updateData,
      }),
      invalidatesTags: ["Feedback"],
    }),
    // Ops Feedback Center
    getFeedbackOverview: build.query({
      query: () => "feedback/ops/overview",
      providesTags: ["Feedback"],
    }),
    getFeedbackInbox: build.query({
      query: (params = {}) => ({
        url: "feedback/ops/inbox",
        params,
      }),
      providesTags: ["Feedback"],
    }),
    getOpsFeedbackById: build.query({
      query: (id) => `feedback/ops/${id}`,
      providesTags: (result, error, id) => [{ type: "Feedback", id }],
    }),
    createOpsFeedback: build.mutation({
      query: (body) => ({
        url: "feedback/ops",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Feedback", "Notifications"],
    }),
    updateOpsFeedback: build.mutation({
      query: ({ id, ...body }) => ({
        url: `feedback/ops/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Feedback", "Notifications"],
    }),
    upsertFeedbackReview: build.mutation({
      query: ({ id, ...body }) => ({
        url: `feedback/ops/${id}/review`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Feedback"],
    }),
    getImprovementActions: build.query({
      query: (params = {}) => ({
        url: "feedback/ops/actions",
        params,
      }),
      providesTags: ["Feedback"],
    }),
    createImprovementAction: build.mutation({
      query: (body) => ({
        url: "feedback/ops/actions",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Feedback", "Notifications"],
    }),
    updateImprovementAction: build.mutation({
      query: ({ id, ...body }) => ({
        url: `feedback/ops/actions/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Feedback", "Notifications"],
    }),
    getFeedbackAnalytics: build.query({
      query: (params = {}) => ({
        url: "feedback/ops/analytics",
        params,
      }),
      providesTags: ["Feedback"],
    }),
    getCallerFeedbackProfile: build.query({
      query: (callerId) => `feedback/ops/caller/${callerId}`,
      providesTags: ["Feedback"],
    }),
    // Work Feedback endpoints (Lead to Member daily feedback)
    getBiddersWithFeedbackStatus: build.query({
      query: () => "work-feedback/bidders",
      providesTags: ["WorkFeedback"],
    }),
    sendWorkFeedback: build.mutation({
      query: (feedbackData) => ({
        url: "work-feedback",
        method: "POST",
        body: feedbackData,
      }),
      invalidatesTags: ["WorkFeedback", "Notifications"],
    }),
    getMyWorkFeedback: build.query({
      query: ({ status, limit } = {}) => ({
        url: "work-feedback/my",
        params: { status, limit },
      }),
      providesTags: ["WorkFeedback"],
    }),
    getYesterdayFeedback: build.query({
      query: () => "work-feedback/yesterday",
      providesTags: ["WorkFeedback"],
    }),
    getSentWorkFeedback: build.query({
      query: ({ status, limit } = {}) => ({
        url: "work-feedback/sent",
        params: { status, limit },
      }),
      providesTags: ["WorkFeedback"],
    }),
    acceptWorkFeedback: build.mutation({
      query: (id) => ({
        url: `work-feedback/${id}/accept`,
        method: "PATCH",
      }),
      invalidatesTags: ["WorkFeedback", "Notifications"],
    }),
    // My Stats endpoint (for members)
    getMyStats: build.query({
      query: () => "bidders/my-stats",
      providesTags: ["MyStats"],
    }),
    // Bidder Dashboard (members only)
    getBidderDashboard: build.query({
      query: () => "bidders/dashboard",
      providesTags: ["MyStats", "Dashboard", "Tasks", "Notifications", "DoneRequests"],
      keepUnusedDataFor: 60,
    }),
    submitDoneRequest: build.mutation({
      query: (body) => ({
        url: "bidders/done-requests",
        method: "POST",
        body,
      }),
      invalidatesTags: ["DoneRequests", "Dashboard", "BidderOperations"],
    }),
    getMyDoneRequest: build.query({
      query: (date) => ({
        url: "bidders/done-requests/today",
        params: date ? { date } : undefined,
      }),
      providesTags: ["DoneRequests"],
    }),
    getPendingDoneRequests: build.query({
      query: ({ date, status } = {}) => ({
        url: "bidders/done-requests/pending",
        params: { date, status },
      }),
      providesTags: ["DoneRequests"],
    }),
    reviewDoneRequest: build.mutation({
      query: ({ id, action, reviewNote }) => ({
        url: `bidders/done-requests/${id}/review`,
        method: "PATCH",
        body: { action, reviewNote },
      }),
      invalidatesTags: ["DoneRequests", "Dashboard", "BidderOperations"],
    }),
    getBidderOperationsSummary: build.query({
      query: (date) => ({
        url: "bidders/operations/summary",
        params: date ? { date } : undefined,
      }),
      providesTags: ["BidderOperations", "DoneRequests", "BidderProgress"],
      keepUnusedDataFor: 60,
    }),
    getMyBidderProgress: build.query({
      query: (limit) => ({
        url: "bidders/my-progress",
        params: limit ? { limit } : undefined,
      }),
      providesTags: ["BidderProgress"],
    }),
    // Interview Schedule endpoints
    getAllowedProfiles: build.query({
      query: () => "interview-schedules/profiles",
      providesTags: ["InterviewSchedules"],
    }),
    createInterviewSchedule: build.mutation({
      query: (scheduleData) => ({
        url: "interview-schedules",
        method: "POST",
        body: scheduleData,
      }),
      invalidatesTags: ["InterviewSchedules", "Notifications"],
    }),
    getInterviewSchedules: build.query({
      query: ({ limit, status } = {}) => ({
        url: "interview-schedules",
        params: { limit, status },
      }),
      providesTags: ["InterviewSchedules"],
    }),
    getTodayInterviewSchedule: build.query({
      query: () => "interview-schedules/today",
      providesTags: ["InterviewSchedules"],
    }),
    getTomorrowInterviewSchedule: build.query({
      query: () => "interview-schedules/tomorrow",
      providesTags: ["InterviewSchedules"],
    }),
    // Caller endpoints
    getMyInterviews: build.query({
      query: ({ dateFilter = "tomorrow" } = {}) => `interview-schedules/my-interviews?dateFilter=${dateFilter}`,
      providesTags: (result, error, { dateFilter } = {}) => [
        { type: "InterviewSchedules", id: dateFilter || "tomorrow" },
        "InterviewSchedules",
      ],
      refetchOnReconnect: false,
    }),
    submitInterviewResponse: build.mutation({
      query: (responseData) => ({
        url: "interview-schedules/response",
        method: "POST",
        body: responseData,
      }),
      invalidatesTags: ["InterviewSchedules", "Notifications", "AdminInterviews"],
    }),
    getInterviewScheduleById: build.query({
      query: (id) => `interview-schedules/${id}`,
      providesTags: (result, error, id) => [{ type: "InterviewSchedules", id }],
    }),
    // User notifications endpoints
    getAllUserNotifications: build.query({
      query: ({ limit } = {}) => ({
        url: "notifications",
        params: limit ? { limit } : {},
      }),
      providesTags: ["Notifications"],
    }),
    // Interview resync mutation
    resyncInterviews: build.mutation({
      query: ({ fromDate, toDate }) => ({
        url: "sync/interviews/resync",
        method: "POST",
        body: { fromDate, toDate },
      }),
      invalidatesTags: ["Dashboard", "Bidders", "ApplicationsInterviews"],
    }),
    // Profile Mapping endpoints
    getProfileMappings: build.query({
      query: () => "management/profile-mappings",
      providesTags: ["ProfileMappings"],
    }),
    getProfileMappingSources: build.query({
      query: () => "management/profile-mapping-sources",
      providesTags: ["ProfileMappings"],
    }),
    updateProfileMapping: build.mutation({
      query: ({ id, resumeBuilderBidderId, jiracodersBidderId }) => ({
        url: `management/profile-mappings/${id}`,
        method: "PUT",
        body: { resumeBuilderBidderId, jiracodersBidderId },
      }),
      invalidatesTags: ["ProfileMappings", "Bidders", "Dashboard"],
    }),
    // Admin Interviews (all interviews list)
    getInterviews: build.query({
      query: () => "interviews",
      providesTags: ["AdminInterviews"],
      refetchOnReconnect: false,
    }),
    // Caller: my interview list (spreadsheet)
    getMyInterviewList: build.query({
      query: () => "interviews/my",
      providesTags: ["AdminInterviews"],
      refetchOnReconnect: false,
    }),
    // Caller: create own interview (appears in caller spreadsheet and admin list)
    createMyInterview: build.mutation({
      query: (body) => ({
        url: "interviews/my",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminInterviews"],
    }),
    // Caller: update own interview
    updateMyInterview: build.mutation({
      query: ({ id, ...body }) => ({
        url: `interviews/my/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AdminInterviews"],
    }),
    getInterviewOptions: build.query({
      query: () => "interviews/options",
      providesTags: ["AdminInterviews"],
    }),
    createInterview: build.mutation({
      query: (body) => ({
        url: "interviews",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminInterviews"],
    }),
    updateInterview: build.mutation({
      query: ({ id, ...body }) => ({
        url: `interviews/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AdminInterviews"],
    }),
    deleteInterview: build.mutation({
      query: (id) => ({
        url: `interviews/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminInterviews"],
    }),
    // Job applications (Jiracoders PJU proxy). Call with { page, limit, keyword }; no hardcoded limit.
    getJobApplications: build.query({
      query: ({ page, limit, keyword = "" }) => ({
        url: "applications",
        params: { page, limit, keyword },
      }),
      providesTags: ["Applications"],
    }),
    getJobApplicationDetails: build.query({
      query: (id) => `applications/${id}/details`,
    }),
    getJobApplicationsOther: build.query({
      query: ({ page, limit, keyword = "" }) => ({
        url: "applications/other",
        params: { page, limit, keyword },
      }),
      providesTags: ["Applications"],
    }),
    getJobApplicationDetailsOther: build.query({
      query: (id) => `applications/other/${id}/details`,
    }),
    // Business Management / Role Projects
    getRoleProjects: build.query({
      query: (params) => ({ url: "role-projects", params }),
      providesTags: ["RoleProjects"],
    }),
    getRoleProjectById: build.query({
      query: (id) => `role-projects/${id}`,
      providesTags: (_r, _e, id) => [{ type: "RoleProjects", id }],
    }),
    getRoleProjectsOverview: build.query({
      query: (params) => ({ url: "role-projects/overview", params }),
      providesTags: ["RoleProjects"],
    }),
    getBusinessSnapshot: build.query({
      query: () => "role-projects/snapshot",
      providesTags: ["RoleProjects"],
    }),
    getRolePaymentSchedule: build.query({
      query: (params) => ({ url: "role-projects/payment-schedule", params }),
      providesTags: ["RoleProjects"],
    }),
    getRoleProjectCallers: build.query({
      query: () => "role-projects/callers",
      providesTags: ["Callers"],
    }),
    createRoleProject: build.mutation({
      query: (body) => ({ url: "role-projects", method: "POST", body }),
      invalidatesTags: ["RoleProjects", "Dashboard"],
    }),
    updateRoleProject: build.mutation({
      query: ({ id, ...body }) => ({ url: `role-projects/${id}`, method: "PATCH", body }),
      invalidatesTags: ["RoleProjects", "Dashboard"],
    }),
    updateRolePipelineStage: build.mutation({
      query: ({ id, pipelineStage }) => ({
        url: `role-projects/${id}/pipeline-stage`,
        method: "PATCH",
        body: { pipelineStage },
      }),
      invalidatesTags: ["RoleProjects", "Dashboard"],
    }),
    activateRoleProject: build.mutation({
      query: (id) => ({ url: `role-projects/${id}/activate`, method: "POST" }),
      invalidatesTags: ["RoleProjects", "Dashboard"],
    }),
    processAutoRoleActivations: build.mutation({
      query: () => ({ url: "role-projects/process-auto-starts", method: "POST" }),
      invalidatesTags: ["RoleProjects", "Dashboard"],
    }),
    updateRolePaymentStatus: build.mutation({
      query: ({ id, paymentStatus }) => ({
        url: `role-projects/${id}/payment-status`,
        method: "PATCH",
        body: { paymentStatus },
      }),
      invalidatesTags: ["RoleProjects"],
    }),
    deleteRoleProject: build.mutation({
      query: (id) => ({ url: `role-projects/${id}`, method: "DELETE" }),
      invalidatesTags: ["RoleProjects", "Dashboard"],
    }),

    getBankAccounts: build.query({
      query: (params) => ({ url: "bank-accounts", params }),
      providesTags: ["BankAccounts"],
    }),
    getBankAccountById: build.query({
      query: (id) => `bank-accounts/${id}`,
      providesTags: (_r, _e, id) => [{ type: "BankAccounts", id }],
    }),
    createBankAccount: build.mutation({
      query: (body) => ({ url: "bank-accounts", method: "POST", body }),
      invalidatesTags: ["BankAccounts"],
    }),
    updateBankAccount: build.mutation({
      query: ({ id, ...body }) => ({ url: `bank-accounts/${id}`, method: "PATCH", body }),
      invalidatesTags: ["BankAccounts"],
    }),
    updateBankAccountStatus: build.mutation({
      query: ({ id, status }) => ({
        url: `bank-accounts/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["BankAccounts"],
    }),
    deleteBankAccount: build.mutation({
      query: (id) => ({ url: `bank-accounts/${id}`, method: "DELETE" }),
      invalidatesTags: ["BankAccounts"],
    }),

    getOpsTasks: build.query({
      query: () => "ops/tasks",
      providesTags: ["Ops"],
    }),
    createOpsTask: build.mutation({
      query: (body) => ({ url: "ops/tasks", method: "POST", body }),
      invalidatesTags: ["Ops"],
    }),
    updateOpsTask: build.mutation({
      query: ({ id, ...body }) => ({ url: `ops/tasks/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Ops"],
      async onQueryStarted({ id, column }, { dispatch, queryFulfilled }) {
        if (column === undefined) return;
        const patch = dispatch(
          api.util.updateQueryData("getOpsTasks", undefined, (draft) => {
            const task = draft.find((t) => t._id === id);
            if (task) task.column = column;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    deleteOpsTask: build.mutation({
      query: (id) => ({ url: `ops/tasks/${id}`, method: "DELETE" }),
      invalidatesTags: ["Ops"],
    }),
    clearCompletedOpsTasks: build.mutation({
      query: () => ({ url: "ops/tasks/completed", method: "DELETE" }),
      invalidatesTags: ["Ops"],
    }),
    getFollowUps: build.query({
      query: () => "ops/follow-ups",
      providesTags: ["Ops"],
    }),
    createFollowUp: build.mutation({
      query: (body) => ({ url: "ops/follow-ups", method: "POST", body }),
      invalidatesTags: ["Ops"],
    }),
    updateFollowUp: build.mutation({
      query: ({ id, ...body }) => ({ url: `ops/follow-ups/${id}`, method: "PATCH", body }),
      invalidatesTags: ["Ops"],
    }),
    deleteFollowUp: build.mutation({
      query: (id) => ({ url: `ops/follow-ups/${id}`, method: "DELETE" }),
      invalidatesTags: ["Ops"],
    }),
    getOpsPipelineTasks: build.query({
      query: () => "ops/pipeline-tasks",
      providesTags: ["Ops", "RoleProjects"],
    }),
    getUpcomingStarts: build.query({
      query: () => "ops/upcoming-starts",
      providesTags: ["Ops", "RoleProjects"],
    }),
    getOpsRoleOptions: build.query({
      query: () => "ops/role-options",
      providesTags: ["Ops", "RoleProjects"],
    }),
    getOpsAssignees: build.query({
      query: () => "ops/assignees",
      providesTags: ["Ops"],
    }),
    getTwoFactorPhones: build.query({
      query: () => "auth/2fa/phones",
      providesTags: ["TwoFactorPhones"],
    }),
    createTwoFactorPhone: build.mutation({
      query: (body) => ({ url: "auth/2fa/phones", method: "POST", body }),
      invalidatesTags: ["TwoFactorPhones"],
    }),
    updateTwoFactorPhone: build.mutation({
      query: ({ id, ...body }) => ({
        url: `auth/2fa/phones/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["TwoFactorPhones"],
    }),
    deleteTwoFactorPhone: build.mutation({
      query: (id) => ({ url: `auth/2fa/phones/${id}`, method: "DELETE" }),
      invalidatesTags: ["TwoFactorPhones"],
    }),

    // Hiring (outsource-dev Interview Copilot)
    getHiringRoles: build.query({
      query: (params) => ({ url: "hiring/roles", params }),
      providesTags: ["HiringRoles"],
    }),
    getHiringRole: build.query({
      query: (id) => `hiring/roles/${id}`,
      providesTags: (_r, _e, id) => [{ type: "HiringRoles", id }],
    }),
    createHiringRole: build.mutation({
      query: (body) => ({ url: "hiring/roles", method: "POST", body }),
      invalidatesTags: ["HiringRoles"],
    }),
    updateHiringRole: build.mutation({
      query: ({ id, ...body }) => ({ url: `hiring/roles/${id}`, method: "PATCH", body }),
      invalidatesTags: ["HiringRoles"],
    }),
    deleteHiringRole: build.mutation({
      query: (id) => ({ url: `hiring/roles/${id}`, method: "DELETE" }),
      invalidatesTags: ["HiringRoles"],
    }),
    getHiringAiConfig: build.query({
      query: () => "hiring/ai-config",
      providesTags: ["HiringAiConfig"],
    }),
    updateHiringAiConfig: build.mutation({
      query: (body) => ({ url: "hiring/ai-config", method: "PUT", body }),
      invalidatesTags: ["HiringAiConfig"],
    }),
    testHiringAiConfig: build.mutation({
      query: (body) => ({ url: "hiring/ai-config/test", method: "POST", body }),
      invalidatesTags: ["HiringAiConfig"],
    }),
    issueHiringExtensionToken: build.mutation({
      query: () => ({ url: "hiring/extension/token", method: "POST" }),
    }),
    getHiringInterviews: build.query({
      query: (params) => ({ url: "hiring/interviews", params }),
      providesTags: ["HiringInterviews"],
    }),
    getHiringInterview: build.query({
      query: (id) => `hiring/interviews/${id}`,
      providesTags: (_r, _e, id) => [{ type: "HiringInterviews", id }],
    }),
    deleteHiringInterview: build.mutation({
      query: (id) => ({ url: `hiring/interviews/${id}`, method: "DELETE" }),
      invalidatesTags: ["HiringInterviews"],
    }),

    // Resume Ops — proxied Endurova resume-generation admin API.
    // Profiles are labelled "Documents" and upstream users "Workers" in the UI.
    getResumeOpsStatus: build.query({
      query: () => "resume-admin/status",
      providesTags: ["ResumeOpsStatus"],
    }),
    // Signing in or out changes which Endurova identity every other call runs
    // under, so both invalidate the whole section.
    createResumeOpsSession: build.mutation({
      query: (body) => ({ url: "resume-admin/session", method: "POST", body }),
      invalidatesTags: [
        "ResumeOpsStatus",
        "ResumeOpsOverview",
        "ResumeOpsProfiles",
        "ResumeOpsWorkers",
        "ResumeOpsApplications",
        "ResumeOpsKeywords",
        "ResumeOpsSettings",
      ],
    }),
    deleteResumeOpsSession: build.mutation({
      query: () => ({ url: "resume-admin/session", method: "DELETE" }),
      invalidatesTags: [
        "ResumeOpsStatus",
        "ResumeOpsOverview",
        "ResumeOpsProfiles",
        "ResumeOpsWorkers",
        "ResumeOpsApplications",
        "ResumeOpsKeywords",
        "ResumeOpsSettings",
      ],
    }),
    getResumeOpsOverview: build.query({
      query: (params) => ({ url: "resume-admin/overview", params }),
      providesTags: ["ResumeOpsOverview"],
    }),

    getResumeOpsProfiles: build.query({
      query: () => "resume-admin/profiles",
      providesTags: ["ResumeOpsProfiles"],
    }),
    getResumeOpsProfile: build.query({
      query: (id) => `resume-admin/profiles/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ResumeOpsProfiles", id }],
    }),
    createResumeOpsProfile: build.mutation({
      query: (body) => ({ url: "resume-admin/profiles", method: "POST", body }),
      invalidatesTags: ["ResumeOpsProfiles", "ResumeOpsOverview"],
    }),
    updateResumeOpsProfile: build.mutation({
      query: ({ id, ...body }) => ({ url: `resume-admin/profiles/${id}`, method: "PATCH", body }),
      invalidatesTags: ["ResumeOpsProfiles", "ResumeOpsOverview"],
    }),
    deleteResumeOpsProfile: build.mutation({
      query: (id) => ({ url: `resume-admin/profiles/${id}`, method: "DELETE" }),
      invalidatesTags: ["ResumeOpsProfiles", "ResumeOpsOverview", "ResumeOpsWorkers"],
    }),
    uploadResumeOpsTemplate: build.mutation({
      query: ({ id, filename, contentBase64 }) => ({
        url: `resume-admin/profiles/${id}/template`,
        method: "PUT",
        body: { filename, contentBase64 },
      }),
      invalidatesTags: ["ResumeOpsProfiles"],
    }),
    deleteResumeOpsTemplate: build.mutation({
      query: (id) => ({ url: `resume-admin/profiles/${id}/template`, method: "DELETE" }),
      invalidatesTags: ["ResumeOpsProfiles"],
    }),

    getResumeOpsWorkers: build.query({
      query: () => "resume-admin/workers",
      providesTags: ["ResumeOpsWorkers"],
    }),
    createResumeOpsWorker: build.mutation({
      query: (body) => ({ url: "resume-admin/workers", method: "POST", body }),
      invalidatesTags: ["ResumeOpsWorkers", "ResumeOpsProfiles"],
    }),
    updateResumeOpsWorker: build.mutation({
      query: ({ id, ...body }) => ({ url: `resume-admin/workers/${id}`, method: "PATCH", body }),
      invalidatesTags: ["ResumeOpsWorkers", "ResumeOpsProfiles"],
    }),
    deleteResumeOpsWorker: build.mutation({
      query: (id) => ({ url: `resume-admin/workers/${id}`, method: "DELETE" }),
      invalidatesTags: ["ResumeOpsWorkers", "ResumeOpsProfiles"],
    }),

    getResumeOpsApplications: build.query({
      query: (params) => ({ url: "resume-admin/applications", params }),
      providesTags: ["ResumeOpsApplications"],
    }),
    getResumeOpsApplication: build.query({
      query: (id) => `resume-admin/applications/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ResumeOpsApplications", id }],
    }),
    updateResumeOpsApplication: build.mutation({
      query: ({ id, ...body }) => ({
        url: `resume-admin/applications/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ResumeOpsApplications"],
    }),
    deleteResumeOpsApplication: build.mutation({
      query: (id) => ({ url: `resume-admin/applications/${id}`, method: "DELETE" }),
      invalidatesTags: ["ResumeOpsApplications", "ResumeOpsProfiles", "ResumeOpsOverview"],
    }),

    getResumeOpsRejectKeywords: build.query({
      query: () => "resume-admin/reject-keywords",
      providesTags: ["ResumeOpsKeywords"],
    }),
    createResumeOpsRejectKeyword: build.mutation({
      query: (body) => ({ url: "resume-admin/reject-keywords", method: "POST", body }),
      invalidatesTags: ["ResumeOpsKeywords"],
    }),
    deleteResumeOpsRejectKeyword: build.mutation({
      query: (id) => ({ url: `resume-admin/reject-keywords/${id}`, method: "DELETE" }),
      invalidatesTags: ["ResumeOpsKeywords"],
    }),

    getResumeOpsSettings: build.query({
      query: () => "resume-admin/settings",
      providesTags: ["ResumeOpsSettings"],
    }),
    // Plaintext key — fetched lazily on an explicit click and never cached.
    revealResumeOpsSettings: build.query({
      query: () => ({ url: "resume-admin/settings", params: { reveal: 1 } }),
      keepUnusedDataFor: 0,
    }),
    updateResumeOpsSettings: build.mutation({
      query: (body) => ({ url: "resume-admin/settings", method: "PUT", body }),
      invalidatesTags: ["ResumeOpsSettings"],
    }),
  }),
});

// export api endpoints
export const {
  useGetUserQuery,
  useGetProductsQuery,
  useGetCustomersQuery,
  useGetTransactionsQuery,
  useGetGeographyQuery,
  useGetSalesQuery,
  useGetAdminsQuery,
  useGetLeadsQuery,
  useGetCallersQuery,
  useUpdateCallerMutation,
  useDeleteCallerMutation,
  useGetCallerProfilesQuery,
  useGetUserPerformanceQuery,
  useGetPerformanceDashboardQuery,
  useGetCallerAssignmentsQuery,
  useGetDashboardQuery,
  useGetDashboardStatsQuery,
  useGetAdminDashboardQuery,
  useGetTeamPresenceQuery,
  usePresenceHeartbeatMutation,
  // Bidder endpoints
  useGetBiddersQuery,
  useGetBidderByIdQuery,
  useGetBidderMetricsQuery,
  useGetBidderActivityQuery,
  useGetSummaryReportQuery,
  useLogApplicationMutation,
  useLogInterviewMutation,
  useRecordAttendanceMutation,
  useGetBidderProgressQuery,
  useGetBidderProgressAggregateQuery,
  useCreateBidderProgressMutation,
  useUpdateBidderProgressMutation,
  useDeleteBidderProgressMutation,
  useBulkSaveBidderProgressMutation,
  useRegisterUserMutation,
  useUpdateMemberStatusMutation,
  useRemoveMemberMutation,
  // Calendar endpoints
  useGetCalendarAuthUrlQuery,
  useGetCalendarStatusQuery,
  useGetTeamCalendarEventsQuery,
  useDisconnectCalendarMutation,
  useSyncCalendarMutation,
  useUpdateCalendarColorMutation,
  // Admin endpoints
  useGetSalaryDataQuery,
  useGetApplicationsInterviewsQuery,
  useGetAttendanceDataQuery,
  // Invoice endpoints
  useGetInvoicesQuery,
  useGetInvoiceByIdQuery,
  useGetInvoiceAnalyticsQuery,
  useCreateInvoiceMutation,
  useApproveInvoiceMutation,
  useDeclineInvoiceMutation,
  useRequestInvoicePayoutMutation,
  useMarkInvoicePaidMutation,
  useCancelInvoiceMutation,
  useGetInvoiceActivityQuery,
  useGetPaymentProfileQuery,
  useUpsertPaymentProfileMutation,
  useGetFinanceOverviewQuery,
  useCreateCallerInvoiceMutation,
  useUpdateInvoiceMutation,
  useSubmitInvoiceMutation,
  // Cash Flow endpoints
  useGetCashFlowQuery,
  useCreateIncomeMutation,
  useUpdateIncomeMutation,
  useDeleteIncomeMutation,
  useCreateCryptoPaymentMutation,
  useImportCryptoPaymentsMutation,
  useUpdateCryptoPaymentMutation,
  useDeleteCryptoPaymentMutation,
  useBulkDeleteCashFlowMutation,
  useGetPaymentSectorsQuery,
  useCreatePaymentSectorMutation,
  useUpdatePaymentSectorMutation,
  useDeletePaymentSectorMutation,
  useGetAccountsQuery,
  useGetAccountsSummaryQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useDeleteAccountMutation,
  // Task endpoints
  useGetFixedTasksQuery,
  useCreateFixedTaskMutation,
  useUpdateFixedTaskMutation,
  useDeleteFixedTaskMutation,
  useGetAdditionalTasksQuery,
  useGetAllAdditionalTasksQuery,
  useCreateAdditionalTaskMutation,
  useUpdateTaskStatusMutation,
  useUpdateKanbanColumnMutation,
  useNotifyTaskUpdateMutation,
  useApproveTaskUpdateMutation,
  useDeclineTaskUpdateMutation,
  useGetNotificationsQuery,
  useGetNotificationCenterQuery,
  useGetNotificationSummaryQuery,
  useGetNotificationCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useNotificationActionMutation,
  // Feedback endpoints
  useSubmitFeedbackMutation,
  useGetMyFeedbacksQuery,
  useGetAllFeedbacksQuery,
  useAcceptFeedbackMutation,
  useReplyToFeedbackMutation,
  useGetPendingUpdatesQuery,
  useUpdatePendingUpdateMutation,
  useGetFeedbackOverviewQuery,
  useGetFeedbackInboxQuery,
  useGetOpsFeedbackByIdQuery,
  useCreateOpsFeedbackMutation,
  useUpdateOpsFeedbackMutation,
  useUpsertFeedbackReviewMutation,
  useGetImprovementActionsQuery,
  useCreateImprovementActionMutation,
  useUpdateImprovementActionMutation,
  useGetFeedbackAnalyticsQuery,
  useGetCallerFeedbackProfileQuery,
  // Work Feedback endpoints
  useGetBiddersWithFeedbackStatusQuery,
  useSendWorkFeedbackMutation,
  useGetMyWorkFeedbackQuery,
  useGetYesterdayFeedbackQuery,
  useGetSentWorkFeedbackQuery,
  useAcceptWorkFeedbackMutation,
  // My Stats endpoint
  useGetMyStatsQuery,
  // Bidder Dashboard (members only)
  useGetBidderDashboardQuery,
  useSubmitDoneRequestMutation,
  useGetMyDoneRequestQuery,
  useGetPendingDoneRequestsQuery,
  useReviewDoneRequestMutation,
  useGetBidderOperationsSummaryQuery,
  useGetMyBidderProgressQuery,
  // Interview Schedule endpoints
  useGetAllowedProfilesQuery,
  useCreateInterviewScheduleMutation,
  useGetInterviewSchedulesQuery,
  useGetTodayInterviewScheduleQuery,
  useGetTomorrowInterviewScheduleQuery,
  useGetMyInterviewsQuery,
  useSubmitInterviewResponseMutation,
  useGetInterviewScheduleByIdQuery,
  useGetJobApplicationsQuery,
  useLazyGetJobApplicationDetailsQuery,
  useGetJobApplicationsOtherQuery,
  useLazyGetJobApplicationDetailsOtherQuery,
  // User notifications
  useGetAllUserNotificationsQuery,
  // Interview resync
  useResyncInterviewsMutation,
  // Profile Mapping endpoints
  useGetProfileMappingsQuery,
  useGetProfileMappingSourcesQuery,
  useUpdateProfileMappingMutation,
  // Admin Interviews
  useGetInterviewsQuery,
  useGetMyInterviewListQuery,
  useCreateMyInterviewMutation,
  useUpdateMyInterviewMutation,
  useGetInterviewOptionsQuery,
  useCreateInterviewMutation,
  useUpdateInterviewMutation,
  useDeleteInterviewMutation,
  // Business Management
  useGetRoleProjectsQuery,
  useGetRoleProjectByIdQuery,
  useGetRoleProjectsOverviewQuery,
  useGetBusinessSnapshotQuery,
  useGetRolePaymentScheduleQuery,
  useGetRoleProjectCallersQuery,
  useCreateRoleProjectMutation,
  useUpdateRoleProjectMutation,
  useUpdateRolePipelineStageMutation,
  useActivateRoleProjectMutation,
  useProcessAutoRoleActivationsMutation,
  useUpdateRolePaymentStatusMutation,
  useDeleteRoleProjectMutation,
  useGetBankAccountsQuery,
  useGetBankAccountByIdQuery,
  useCreateBankAccountMutation,
  useUpdateBankAccountMutation,
  useUpdateBankAccountStatusMutation,
  useDeleteBankAccountMutation,
  useGetOpsTasksQuery,
  useCreateOpsTaskMutation,
  useUpdateOpsTaskMutation,
  useDeleteOpsTaskMutation,
  useClearCompletedOpsTasksMutation,
  useGetFollowUpsQuery,
  useCreateFollowUpMutation,
  useUpdateFollowUpMutation,
  useDeleteFollowUpMutation,
  useGetOpsPipelineTasksQuery,
  useGetUpcomingStartsQuery,
  useGetOpsRoleOptionsQuery,
  useGetOpsAssigneesQuery,
  useGetTwoFactorPhonesQuery,
  useCreateTwoFactorPhoneMutation,
  useUpdateTwoFactorPhoneMutation,
  useDeleteTwoFactorPhoneMutation,
  useGetHiringRolesQuery,
  useGetHiringRoleQuery,
  useCreateHiringRoleMutation,
  useUpdateHiringRoleMutation,
  useDeleteHiringRoleMutation,
  useGetHiringAiConfigQuery,
  useUpdateHiringAiConfigMutation,
  useTestHiringAiConfigMutation,
  useIssueHiringExtensionTokenMutation,
  useGetHiringInterviewsQuery,
  useGetHiringInterviewQuery,
  useDeleteHiringInterviewMutation,
  // Resume Ops
  useGetResumeOpsStatusQuery,
  useCreateResumeOpsSessionMutation,
  useDeleteResumeOpsSessionMutation,
  useGetResumeOpsOverviewQuery,
  useGetResumeOpsProfilesQuery,
  useGetResumeOpsProfileQuery,
  useCreateResumeOpsProfileMutation,
  useUpdateResumeOpsProfileMutation,
  useDeleteResumeOpsProfileMutation,
  useUploadResumeOpsTemplateMutation,
  useDeleteResumeOpsTemplateMutation,
  useGetResumeOpsWorkersQuery,
  useCreateResumeOpsWorkerMutation,
  useUpdateResumeOpsWorkerMutation,
  useDeleteResumeOpsWorkerMutation,
  useGetResumeOpsApplicationsQuery,
  useLazyGetResumeOpsApplicationQuery,
  useUpdateResumeOpsApplicationMutation,
  useDeleteResumeOpsApplicationMutation,
  useGetResumeOpsRejectKeywordsQuery,
  useCreateResumeOpsRejectKeywordMutation,
  useDeleteResumeOpsRejectKeywordMutation,
  useGetResumeOpsSettingsQuery,
  useLazyRevealResumeOpsSettingsQuery,
  useUpdateResumeOpsSettingsMutation,
} = api;
